import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { PanelFrame } from '../panel-frame.js';
import type { PanelId, TabId } from '../store.js';

// Methodology — a static, read-only reference panel (Record-styled, internal
// tabs) describing the technical basis of the platform's proprietary metrics.
// Snapshot altitude: sophisticated but readable; composition + method, not the
// trained weights/coefficients. Data provenance lives in its own market-
// filterable Sources panel (panels/sources.tsx).

function H({ children }: { children: React.ReactNode }) {
  return <div className="mth-h">{children}</div>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="mth-p">{children}</p>;
}
function Formula({ children }: { children: React.ReactNode }) {
  return <div className="mth-formula">{children}</div>;
}
function Note({ children }: { children: React.ReactNode }) {
  return <div className="mth-note">{children}</div>;
}
function Defn({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="mth-defn">
      <span className="mth-defn-t">{term}</span>
      <span className="mth-defn-d">{children}</span>
    </div>
  );
}

// --- Revenue Estimate -------------------------------------------------------
function RevenueTab() {
  return (
    <div className="mth-doc">
      <P>
        Every company carries a revenue figure and a confidence rating <b>P</b> (0–100). Where a
        company publicly discloses turnover, the figure shown is the <b>reported</b> value and
        P&nbsp;=&nbsp;<b>100</b>. For the majority of private operators that do not file turnover,
        the figure is <b>modelled</b> and labelled accordingly.
      </P>

      <H>The model</H>
      <P>
        A per-subsegment log-linear regression of turnover on company size, fitted only on the
        operators within each healthcare subsegment that <i>do</i> report turnover:
      </P>
      <Formula>log(turnover) = a + b₁·log(employees) + b₂·log(total&nbsp;assets)</Formula>
      <P>
        with single-predictor fallbacks where one input is missing. The displayed figure is the{' '}
        <b>median of the predictive distribution</b>, rounded and capped at the turnover ceiling
        implied by the company&apos;s filing size class. The model is refitted annually as new
        filings arrive, per market.
      </P>

      <H>Confidence P</H>
      <P>
        P is the calibrated probability that true turnover lies <b>within ±50%</b> (a factor of 1.5,
        i.e. |log₁₀(actual/predicted)| ≤ log₁₀1.5) of the figure shown. It is the empirical fraction
        of out-of-fold predictions inside that band, per subsegment, via{' '}
        <b>five-fold cross-validation</b> — not derived analytically from a fitted variance. The
        ±50% band is a fixed platform standard so P is comparable across markets.
      </P>

      <H>Per-row precedence</H>
      <P>The figure shown for any one company resolves down a fixed ladder:</P>
      <Defn term="1 · Reported">disclosed turnover or an explicit nil/dormant filing → P100.</Defn>
      <Defn term="2 · Enhanced predictor">
        where a sector-specific scale signal exists (beds, dispensing volume, patient-list size), it
        conditions the estimate and its confidence.
      </Defn>
      <Defn term="3 · Subsegment model">the log-linear size model above, at the subsegment P.</Defn>
      <Defn term="4 · Limited data">thin inputs fall back to a lower, constant pooled P.</Defn>

      <Note>
        Estimates are modelled indicators, not audited figures — built for ranking and screening,
        not valuation. The estimate and its P travel together; never read the figure without the
        rating.
      </Note>
    </div>
  );
}

// --- Corryk Seller Readiness Score (entity-level) ---------------------------
function SellerReadinessTab() {
  return (
    <div className="mth-doc">
      <P>
        The <b>Corryk Seller Readiness Score</b> (0–100) reads how likely an independent
        operator&apos;s owner is <b>ready or under pressure to transact</b> — gettability, not
        attractiveness. It is the transparent companion to the Origination Signal: the Signal ranks{' '}
        <i>who is worth pursuing</i>; the Seller Readiness Score reads{' '}
        <i>who is approachable, and why</i>.
      </P>

      <H>Population</H>
      <P>
        Scored over the same independent-operator universe as the Origination Signal — corporates,
        resolved roll-up groups, consolidator brands, NHS/charity entities and out-of-scope or
        dissolved companies are excluded.
      </P>

      <H>How it composes</H>
      <P>
        Each factor maps an observable to a 0–1 <b>exit-pressure</b> sub-score. The index{' '}
        <b>accumulates</b> those signals rather than averaging them: a single strong signal — an
        aging sole owner — stands on its own, further distress compounds it, and calm factors do not
        drag it down. The score is <b>absolute</b> (not a within-country percentile), so a given
        number means the same thing across the universe and over time — which is what lets it drive
        a fixed filter and change-alerts.
      </P>

      <H>Factors</H>
      <P>Five seller-side factors (composition, not the fixed weights):</P>
      <Defn term="Succession">
        owner age (65+ carries readiness), owner-managed control, tenure.
      </Defn>
      <Defn term="Board fragility">a sole or thin board with no obvious successor.</Defn>
      <Defn term="Financial pressure">overdue accounts, declining filed revenue.</Defn>
      <Defn term="Regulatory distress">
        a CQC <i>Inadequate</i> / <i>Requires improvement</i> rating, or a weak normalised quality
        tier.
      </Defn>
      <Defn term="Secured debt">
        charges / debentures — a financing relationship that can catalyse a sale.
      </Defn>
      <Note>
        Some inputs (overdue accounts, owner age) also feed the Origination Signal — but here
        distress reads as <b>motivation</b>, the opposite of the quality red flag it represents for
        attractiveness.
      </Note>

      <H>Confidence</H>
      <P>
        A <b>data-completeness</b> rating (High / Moderate / Indicative). Owner age by proxy or thin
        coverage lowers it; a factor with no data is dropped from the blend and confidence is
        downgraded — it is never scored as a zero.
      </P>

      <Note>
        Coverage: all 27 markets, at a per-market confidence tier that reflects each market&apos;s
        data depth (the label carries it). A screening signal — not investment advice, and not
        evidence that a company is for sale.
      </Note>
    </div>
  );
}

// --- Corryk Origination Signal (entity-level) -------------------------------
function SignalTab() {
  return (
    <div className="mth-doc">
      <P>
        The <b>Corryk Origination Signal</b> is an entity-level score (0–100) of how target-like an
        independent operator is — its acquisition attractiveness — computed natively per company
        from a frozen, cross-validated model. It is the <i>entity</i> counterpart to the
        region-level Opportunity Index; the two share branding but never the same grain.
      </P>

      <H>Population</H>
      <P>
        Scored over independent operators only. Corporates and resolved roll-up groups, consolidator
        brands, NHS/charity/non-commercial entities, and out-of-scope or dissolved companies are
        excluded structurally and by name before scoring.
      </P>

      <H>Two-pass computation</H>
      <Defn term="Pass 1 — propensity">
        a calibrated logistic model: propensity = σ(intercept + Σ&nbsp;coefᵢ·featureᵢ), per entity,
        stateless. Coefficients are frozen per market and validated <b>out of time</b>: a market
        model must forecast deals dated <i>after</i> its training window (temporal holdout), not
        merely recognise the deals it trained on. A model that fails that gate serves capped at
        Indicative, whatever its cross-validation score.
      </Defn>
      <Defn term="Pass 2 — signal">
        each propensity is converted to a <b>within-country percentile</b> → the 0–100 Signal. The
        score is therefore population-relative: it ranks an operator against its national peer set.
      </Defn>

      <H>Feature families</H>
      <P>The predictor vector groups into four families (composition, not the trained weights):</P>
      <Defn term="Scale">turnover, total assets, employees (log, capped).</Defn>
      <Defn term="Succession">
        owner age (65+ carries succession readiness) and control structure.
      </Defn>
      <Defn term="Filing health">accounts recency, overdue filings, secured debt.</Defn>
      <Defn term="Local consolidation">
        the share of the local market already held by chains (multi-site providers) and the density
        of chains nearby — the strongest signals in the model.
      </Defn>

      <H>Confidence</H>
      <P>
        A separate <b>data-completeness</b> rating (High / Moderate) — distinct from the Signal
        level. Unknown inputs are <b>zero-coded and penalised</b>, never mean-imputed, so a
        thinly-filed company scores conservatively and is flagged lower-confidence rather than
        flattered.
      </P>

      <Note>
        Coverage: all 27 markets on the signal ladder — a native model where the data supports one,
        a structural floor (served as &ldquo;Indicative&rdquo;) elsewhere. A screening and ranking
        signal — not investment advice.
      </Note>
    </div>
  );
}

// --- Corryk Opportunity Index (region × subsector) --------------------------
function IndexTab() {
  return (
    <div className="mth-doc">
      <P>
        The <b>Corryk Opportunity Index</b> is a score (0–100) of how attractive a{' '}
        <b>region × subsector</b> is to build a platform in. It is computed on a country × region ×
        subsegment grid and is distinct from the entity-level Origination Signal.
      </P>

      <H>Composition — six factors</H>
      <P>Each factor is normalised to [0,&nbsp;1]:</P>
      <Defn term="Fragmentation">
        inverse market concentration (1 − CR5 over resolved roll-up groups).
      </Defn>
      <Defn term="White space">share of independents in cells with no consolidator presence.</Defn>
      <Defn term="Succession">founder / individual control, ramped by owner age.</Defn>
      <Defn term="Acquisition">
        consolidator activity in the cell (v1 proxy: distinct-consolidator count; a live deal feed
        is on the roadmap).
      </Defn>
      <Defn term="Momentum">multi-year trajectory and reimbursement tilt (roadmap).</Defn>
      <Defn term="Workforce">labour supply and pressure (roadmap).</Defn>

      <H>The blend</H>
      <P>Factors combine by a confidence-weighted average:</P>
      <Formula>Index = 100 · Σ(wᵢ·cᵢ·fᵢ) / Σ(wᵢ·cᵢ)</Formula>
      <P>
        where <b>wᵢ</b> are fixed, proprietary weights, <b>cᵢ</b> is each factor&apos;s data
        confidence, and <b>fᵢ</b> the normalised factor. A factor with no data (cᵢ&nbsp;=&nbsp;0)
        drops out cleanly and is shown as <b>—</b>, never a fake zero. The full six-factor
        decomposition is always visible: composed, not a black box.
      </P>

      <H>Why the weights are fixed</H>
      <P>
        The weighting is our composite — a platform constant, not a user input. Users tune their own
        thesis (deal size, sweet-spot bands) elsewhere; the Index itself is read-only so scores stay
        comparable across the whole map.
      </P>

      <Note>
        Directional and presence-based: bounded by geocoding and ownership-attribution coverage,
        with exact counts behind every cell. Built to prioritise where to look, not to size a deal.
      </Note>
    </div>
  );
}

// --- White Space ------------------------------------------------------------
function WhiteSpaceTab() {
  return (
    <div className="mth-doc">
      <P>
        <b>White space</b> is where demand is served by <b>independents but no consolidator</b> —
        the open territory for a buy-and-build. It is computed per <b>sub-segment</b> (dental gaps
        are not vet gaps) and shown on the Prevalence map.
      </P>

      <H>How it is estimated</H>
      <P>
        Every geocoded site in the chosen sub-segment is binned into a coarse <b>0.25° grid cell</b>{' '}
        (~20–28&nbsp;km), and each operator is resolved to its roll-up group so a chain&apos;s many
        opcos count once. A cell is <b>white space</b> when:
      </P>
      <Defn term="Has demand">it contains one or more independent operators, and</Defn>
      <Defn term="No consolidator">no consolidator-owned site falls in the same cell.</Defn>
      <P>
        It is <b>presence-based</b> — an exact count of independents with zero consolidator
        footprint, not a model. A cell where a consolidator is already present is not white space,
        however many independents remain.
      </P>

      <H>How it is plotted</H>
      <P>Each white-space cell is drawn as a square on the map, dual-encoded:</P>
      <Defn term="Colour">
        the region&apos;s <b>Corryk Opportunity Index</b> — warmer = more attractive to build in, so
        it matches the Indications heatmap exactly.
      </Defn>
      <Defn term="Opacity">
        how many independents sit in the cell — denser opportunity reads stronger.
      </Defn>
      <P>
        Pick a sub-segment (and optionally a country) first, then toggle <b>Show white space</b>:
        the gaps light up over the consolidator footprint so open ground is visible at a glance.
      </P>

      <Note>
        Directional and coarse: bounded by geocoding completeness and ownership-attribution
        coverage, at 0.25° resolution. Exact independent counts sit behind every cell — use it to
        decide where to look, then drill with the Catchment panel.
      </Note>
    </div>
  );
}

// Data provenance lives in its own market-filterable Sources panel now
// (panels/sources.tsx), not as a tab here.

const TABS: Array<{ id: string; label: string; render: () => React.ReactNode }> = [
  { id: 'revenue', label: 'Revenue Estimate', render: RevenueTab },
  { id: 'signal', label: 'Corryk Origination Signal', render: SignalTab },
  { id: 'seller-readiness', label: 'Corryk Seller Readiness Score', render: SellerReadinessTab },
  { id: 'index', label: 'Corryk Opportunity Index', render: IndexTab },
  { id: 'whitespace', label: 'White Space', render: WhiteSpaceTab },
];

export function MethodologyPanel({
  tabId,
  panelId,
  initialState,
}: {
  tabId: TabId;
  panelId: PanelId;
  /** Optional `initialTab` (e.g. opened from the Referrals panel deep-linking
   *  to the Origination Signal tab). Falls back to the first tab. */
  initialState?: Record<string, unknown>;
}) {
  const [tab, setTab] = useState(() => {
    const want = initialState?.['initialTab'];
    return typeof want === 'string' && TABS.some((t) => t.id === want) ? want : 'revenue';
  });
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="mth-root">
        <style>{MTH_CSS}</style>
        <Tabs.Root value={tab} onValueChange={setTab} className="mth-tabs">
          <Tabs.List className="mth-tablist">
            {TABS.map((t) => (
              <Tabs.Trigger key={t.id} value={t.id} className="mth-tab">
                {t.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {TABS.map((t) => (
            <Tabs.Content key={t.id} value={t.id} className="mth-body">
              {t.render()}
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </PanelFrame>
  );
}

const MTH_CSS = `
.mth-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.mth-tabs { display:flex; flex-direction:column; min-height:0; flex:1; }
.mth-tablist { display:flex; gap:16px; padding:8px var(--s-3) 0; border-bottom:1px solid var(--hairline); flex-wrap:wrap; }
.mth-tab { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-tertiary); background:none; border:none; border-bottom:2px solid transparent; padding:4px 0 6px; cursor:pointer; white-space:nowrap; }
.mth-tab[data-state="active"] { color:var(--text-primary); font-weight:600; border-bottom-color:var(--accent); }
.mth-body { flex:1; overflow:auto; padding:var(--s-4) var(--s-3); min-height:0; }
.mth-doc { max-width:720px; }
.mth-h { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.06em; color:var(--text-secondary); font-weight:700; margin:20px 0 7px; border-top:2px solid var(--text-primary); padding-top:7px; }
.mth-doc > .mth-h:first-child { margin-top:0; }
.mth-p { margin:0 0 9px; line-height:1.55; color:var(--text-secondary); }
.mth-p b { color:var(--text-primary); font-weight:600; }
.mth-formula { font-family:var(--font-mono,monospace); font-size:var(--t-body); color:var(--text-primary); background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); padding:9px 11px; margin:2px 0 11px; overflow-x:auto; }
.mth-defn { display:grid; grid-template-columns:170px 1fr; gap:10px; padding:6px 0; border-bottom:1px solid var(--hairline); line-height:1.5; }
.mth-defn:last-of-type { border-bottom:none; }
.mth-defn-t { font-weight:600; color:var(--text-primary); }
.mth-defn-d { color:var(--text-secondary); }
.mth-mono { font-family:var(--font-mono,monospace); font-size:0.95em; }
.mth-note { margin-top:14px; padding:9px 11px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.5; }
`;
